<script lang="ts">
  import { format } from 'date-fns';
  import type { ITransactionDetails } from '$lib/interfaces/ITransactionDetails';
  import { TransactionStatus } from "$lib/enums/transaction-status";
  import { TransactionOrderProgressStatus } from "$lib/enums/transaction-order-progress-status";
  import { dialogData, dialogStates, closeDialog } from "$lib/stores/dialogStores";
  import { TransactionType } from "$lib/enums/transaction-type";
  import TransactionDetailsCardItem from "$lib/components/TransactionDetails/TransactionDetailsCard/TransactionDetailsCardItem.svelte";

  let transaction: ITransactionDetails | null = null;
  let loading = true;
  let error = false;
  let errorMessage = "";
  let transactionId = "";
  export let transactionStatus: keyof typeof TransactionStatus;
  export let transactionType: keyof typeof TransactionType;
  export let txId: string = '';
  export let orderId: string;
  export let createdAt: string;



  function getStatusClass(status: string): string {
    switch (status) {
      case TransactionStatus.CONFIRMED:
        return "text-green-500";
      case TransactionStatus.PENDING:
      case TransactionStatus.PROCESSING:
        return "text-yellow-500";
      case TransactionStatus.DECLINED:
      case TransactionStatus.REJECTED:
      case TransactionStatus.CANCELLED:
        return "text-red-500";
      default:
        return "text-gray-400";
    }
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case TransactionStatus.CONFIRMED:
        return "bg-green-500/20 text-green-500 border-green-500/30";
      case TransactionStatus.PENDING:
      case TransactionStatus.PROCESSING:
        return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
      case TransactionStatus.DECLINED:
      case TransactionStatus.REJECTED:
      case TransactionStatus.CANCELLED:
        return "bg-red-500/20 text-red-500 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  }

  function getProgressStatusClass(status: string): string {
    return status === TransactionOrderProgressStatus.REACHED ? "bg-green-500" : "bg-gray-600";
  }

  function getTransactionTypeIcon(type: string): string {
    switch (type) {
      case TransactionType.DEPOSIT:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                </svg>`;
      case TransactionType.WITHDRAWAL:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>`;
      case TransactionType.BONUS:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zm7-10a1 1 0 01.707.293l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L13.586 9H10a1 1 0 110-2h3.586l-2.293-2.293A1 1 0 0112 2z" clip-rule="evenodd" />
                </svg>`;
      default:
        return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z" clip-rule="evenodd" />
                </svg>`;
    }
  }

  // Extract transaction ID from dialog data
  function extractTransactionId(data: any): string | null {
    console.log("Extracting transaction ID from:", data);

    if (!data) return null;

    // Case 1: Direct string
    if (typeof data === 'string' && data) {
      console.log("Found direct string transaction ID:", data);
      return data;
    }

    // Case 2: Object with orderId property
    if (typeof data === 'object' && data !== null) {
      if (data.orderId && typeof data.orderId === 'string') {
        console.log("Found transaction ID in orderId property:", data.orderId);
        return data.orderId;
      }

      // Case 3: Object with transactionOrderId property
      if (data.transactionOrderId && typeof data.transactionOrderId === 'string') {
        console.log("Found transaction ID in transactionOrderId property:", data.transactionOrderId);
        return data.transactionOrderId;
      }
    }

    console.error("Could not extract transaction ID from data:", data);
    return null;
  }

  async function loadTransactionDetails() {
    console.log("loadTransactionDetails called with ID:", transactionId);

    if (!transactionId) {
      console.error("No transaction ID provided");
      error = true;
      errorMessage = "No transaction ID provided";
      loading = false;
      return;
    }

    try {
      loading = true;
      error = false;
      console.log("Fetching transaction details for ID:", transactionId);

      // Direct fetch approach to bypass any service issues
      const token = document.cookie.split('; ').find(row => row.startsWith('forza_token='))?.split('=')[1];
      if (!token) {
        error = true;
        errorMessage = "Authentication token not found";
        loading = false;
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.forza.bet';
      const url = `${apiUrl}/user/transactions/${transactionId}`;

      console.log("Fetching from URL:", url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      console.log("Raw API response:", data);

      if (data && data.data) {
        transaction = data.data;
        console.log("Transaction details set:", transaction);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Exception fetching transaction details:", err);
      error = true;
      errorMessage = err instanceof Error ? err.message : "Unknown error";
    } finally {
      loading = false;
    }
  }

  function retry() {
    loadTransactionDetails();
  }

  function handleClose() {
    closeDialog("TransactionDetailsModal");
  }

  // Subscribe to dialog data changes
  $: {
    if ($dialogStates.TransactionDetailsModal) {
      console.log("Dialog is open, current dialog data:", $dialogData);
      const dialogValue = $dialogData.TransactionDetailsModal;
      const newTransactionId = extractTransactionId(dialogValue);

      if (newTransactionId) {
        console.log("New transaction ID detected:", newTransactionId);
        if (newTransactionId !== transactionId) {
          transactionId = newTransactionId;
          // Reset state for new transaction
          transaction = null;
          error = false;
          loading = true;
          loadTransactionDetails();
        }
      } else {
        console.error("Invalid transaction ID in dialog data:", dialogValue);
        error = true;
        errorMessage = "Invalid transaction ID";
        loading = false;
      }
    }
  }

</script>

<div class="rounded-lg bg-primary-dark p-4 w-full flex flex-col gap-2">
  <TransactionDetailsCardItem name="Status" value={TransactionStatus[transactionStatus]}/>
  <TransactionDetailsCardItem name="Transaction Type" value={TransactionType[transactionType]}/>
  {#if txId}
    <TransactionDetailsCardItem name="TxId" value={txId} isEllipsis/>
  {/if}
  <TransactionDetailsCardItem name="OrderId" value={orderId} isEllipsis />
  <TransactionDetailsCardItem name="Time" value={format(new Date(createdAt), 'MM/dd/yyyy, h:mm:ss a')}/>

</div>